import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Message } from '../types/chat'
import { User } from '../types/user'
import { Avatar } from './Avatar'
import { ReactionPicker } from './ReactionPicker'
import { formatUserName } from '../utils/name'
import { formatMessageTime, getFullTimestamp, parseMentions } from '../utils/chat'
import './ChatMessage.css'

interface ChatMessageProps {
  message: Message
  sender?: User
  currentUserId?: string
  isOwnMessage: boolean
  allUsers?: User[]
  onEdit?: (messageId: string, newContent: string) => void
  onDelete?: (messageId: string) => void
  onAddReaction?: (messageId: string, emoji: string) => void
  onRemoveReaction?: (messageId: string, emoji: string) => void
}

export function ChatMessage({
  message,
  sender,
  currentUserId,
  isOwnMessage,
  allUsers = [],
  onEdit,
  onDelete,
  onAddReaction,
  onRemoveReaction,
}: ChatMessageProps) {
  const [showActions, setShowActions] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(message.content)
  const [showReactionPicker, setShowReactionPicker] = useState(false)
  const [showReadReceipts, setShowReadReceipts] = useState(false)
  const reactionButtonRef = useRef<HTMLButtonElement>(null)

  // Don't render deleted messages (or show a "message deleted" placeholder)
  if (message.isDeleted) {
    return (
      <div className="chat-message chat-message-deleted">
        <div className="chat-message-content">
          <em>This message was deleted</em>
        </div>
      </div>
    )
  }

  const senderName = sender
    ? formatUserName(sender)
    : isOwnMessage
    ? 'You'
    : 'Unknown'

  const handleEdit = () => {
    if (isEditing && editContent.trim() && editContent !== message.content) {
      onEdit?.(message.id, editContent.trim())
    }
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setEditContent(message.content)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleEdit()
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  const canEdit = isOwnMessage && !message.isDeleted
  const canDelete = isOwnMessage && !message.isDeleted
  const timeSinceCreated = new Date(message.createdAt).getTime()
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000
  const canEditTimeLimit = timeSinceCreated > fiveMinutesAgo

  // Group reactions by emoji
  const reactionsByEmoji = (message.reactions || []).reduce((acc, reaction) => {
    if (!acc[reaction.emoji]) {
      acc[reaction.emoji] = []
    }
    const emojiArray = acc[reaction.emoji]
    if (emojiArray) {
      emojiArray.push(reaction)
    }
    return acc
  }, {} as Record<string, Array<{ emoji: string; userId: string; createdAt: string }>>)

  const handleReactionClick = (emoji: string) => {
    const userReaction = (message.reactions || []).find(
      (r) => r.userId === currentUserId && r.emoji === emoji
    )
    if (userReaction) {
      onRemoveReaction?.(message.id, emoji)
    } else {
      onAddReaction?.(message.id, emoji)
    }
  }

  const handleAddReaction = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setShowReactionPicker(true)
  }

  const handleReactionPickerSelect = (emoji: string) => {
    console.log('Reaction picker selected:', emoji, 'for message:', message.id)
    if (onAddReaction) {
      onAddReaction(message.id, emoji)
    } else {
      console.warn('onAddReaction is not defined')
    }
    setShowReactionPicker(false)
  }

  // Get read receipts for team messages
  const getReadReceipts = () => {
    if (!message.isTeamMessage || !message.readBy) return []
    return message.readBy
      .map((userId) => allUsers.find((u) => u.id === userId))
      .filter((u): u is User => !!u)
  }

  const readReceipts = getReadReceipts()

  // Parse and highlight mentions in message content
  const renderMessageContent = () => {
    if (!message.content) return null

    // Create user map for mention parsing
    const userMap = new Map<string, { username?: string; firstName?: string; lastName?: string }>()
    allUsers.forEach((u) => {
      userMap.set(u.id, {
        username: u.username,
        firstName: u.firstName,
        lastName: u.lastName,
      })
    })

    const mentions = parseMentions(message.content, userMap)
    if (mentions.length === 0) {
      return message.content
    }

    // Build array of text and mention spans
    const parts: (string | JSX.Element)[] = []
    let lastIndex = 0

    mentions.forEach((mention, index) => {
      // Add text before mention
      if (mention.startIndex > lastIndex) {
        parts.push(message.content.substring(lastIndex, mention.startIndex))
      }

      // Add mention span
      parts.push(
        <span key={`mention-${index}`} className="chat-message-mention">
          {mention.text}
        </span>
      )

      lastIndex = mention.endIndex
    })

    // Add remaining text
    if (lastIndex < message.content.length) {
      parts.push(message.content.substring(lastIndex))
    }

    return parts
  }

  return (
    <div
      className={`chat-message ${isOwnMessage ? 'own' : 'other'} ${!message.readAt && !isOwnMessage ? 'unread' : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="chat-message-wrapper">
        {!isOwnMessage && (
          <Avatar
            user={sender}
            size="medium"
            className="chat-message-avatar"
          />
        )}
        <div className="chat-message-content-wrapper">
          <div className="chat-message-header">
            <span className="chat-message-sender">{senderName}</span>
            <span
              className="chat-message-time"
              title={getFullTimestamp(message.createdAt)}
            >
              {formatMessageTime(message.createdAt)}
            </span>
            {message.editedAt && (
              <span className="chat-message-edited" title="Edited">(edited)</span>
            )}
            {message.isTeamMessage && isOwnMessage && readReceipts.length > 0 && (
              <span
                className="chat-message-read-count"
                onMouseEnter={() => setShowReadReceipts(true)}
                onMouseLeave={() => setShowReadReceipts(false)}
                title={`Read by ${readReceipts.length} ${readReceipts.length === 1 ? 'person' : 'people'}`}
              >
                ✓{readReceipts.length}
              </span>
            )}
          </div>
          {isEditing ? (
            <div className="chat-message-edit">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onBlur={handleEdit}
                onKeyDown={handleKeyDown}
                className="chat-message-edit-input"
                autoFocus
                rows={Math.min(editContent.split('\n').length, 10)}
              />
              <div className="chat-message-edit-actions">
                <button
                  onClick={handleEdit}
                  className="chat-message-edit-save"
                  aria-label="Save"
                >
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="chat-message-edit-cancel"
                  aria-label="Cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="chat-message-content">{renderMessageContent()}</div>
          )}
          {(message.reactions && message.reactions.length > 0) || !message.isDeleted ? (
            <div className="chat-message-reactions">
              {Object.entries(reactionsByEmoji).map(([emoji, reactions]) => {
                const userReacted = reactions?.some((r) => r.userId === currentUserId) || false
                return (
                  <button
                    key={emoji}
                    className={`chat-message-reaction ${userReacted ? 'reacted' : ''}`}
                    onClick={() => handleReactionClick(emoji)}
                    title={`${reactions?.length || 0} reaction${reactions && reactions.length !== 1 ? 's' : ''}`}
                  >
                    <span className="chat-message-reaction-emoji">{emoji}</span>
                    <span className="chat-message-reaction-count">{reactions?.length || 0}</span>
                  </button>
                )
              })}
              {!message.isDeleted && (
                <button
                  ref={reactionButtonRef}
                  className="chat-message-reaction-add"
                  onClick={handleAddReaction}
                  aria-label="Add reaction"
                  title="Add reaction"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 3v10M3 8h10"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
              {showReactionPicker && reactionButtonRef.current && createPortal(
                <ReactionPicker
                  onSelect={handleReactionPickerSelect}
                  onClose={() => setShowReactionPicker(false)}
                  position={
                    (() => {
                      const rect = reactionButtonRef.current!.getBoundingClientRect()
                      return {
                        top: rect.bottom + 5,
                        left: rect.left,
                      }
                    })()
                  }
                />,
                document.body
              )}
            </div>
          ) : null}
        </div>
        {isOwnMessage && (
          <Avatar
            user={sender}
            size="medium"
            className="chat-message-avatar"
          />
        )}
      </div>
      {showReadReceipts && message.isTeamMessage && isOwnMessage && readReceipts.length > 0 && (
        <div className="chat-message-read-receipts">
          <div className="chat-message-read-receipts-title">Read by:</div>
          <div className="chat-message-read-receipts-list">
            {readReceipts.map((user) => (
              <div key={user.id} className="chat-message-read-receipt-item">
                <Avatar user={user} size="small" />
                <span>{formatUserName(user)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {showActions && (canEdit || canDelete) && (
        <div className="chat-message-actions">
          {canEdit && canEditTimeLimit && (
            <button
              onClick={() => setIsEditing(true)}
              className="chat-message-action"
              aria-label="Edit message"
              title="Edit"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M11.333 2a1.414 1.414 0 011 2.414L5 12.167l-2.667.666.666-2.666L9.586 3.667a1.414 1.414 0 011-1z"
                  stroke="currentColor"
                  strokeWidth="1.333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete?.(message.id)}
              className="chat-message-action chat-message-action-delete"
              aria-label="Delete message"
              title="Delete"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h10.667z"
                  stroke="currentColor"
                  strokeWidth="1.333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
